import { format } from "date-fns";

import { Logger } from "./logger";
import { S3Bucket } from "./s3_bucket";
import { stringComparatorGenerator } from "./string";

import { S3_BUCKETS } from "config";
import { ForumAPI } from "contexts/forum_api_context";
import { ClassInfoType, LoggerType } from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { UserType } from "shared/types/user";
import { AppDispatch } from "store";
import { createMonitoringEvent } from "store/reducers/monitoring";
import { longDisplayName } from "utils/user";

class BugReporter {
  private classInfo: ClassInfoType;

  private user: UserType;

  private userId: string;

  private otherUsers: { [id: string]: UserType };

  private logger: LoggerType;

  private reportTitle: string;

  private message: string;

  private files: File[];

  private userUploadedFileUrls: string[];

  private bucket: S3Bucket;

  private storeStateString: string;

  private dispatch: AppDispatch;

  private forumAPI: ForumAPI;

  timestamp: number;

  constructor(
    reportTitle: string,
    message: string,
    files: File[],
    users: { [id: string]: UserType },
    currentUserId: string,
    classInfo: ClassInfoType,
    storeStateString: string,
    dispatch: AppDispatch,
    timestamp: number | null = null,
  ) {
    this.logger = window.flightLogger;
    this.reportTitle = reportTitle;
    this.message = message;
    this.files = files;
    this.userUploadedFileUrls = [];
    this.timestamp = timestamp || Date.now();
    this.user = users[currentUserId];
    this.otherUsers = users;
    this.userId = currentUserId;
    this.classInfo = classInfo;
    this.storeStateString = storeStateString;
    this.forumAPI = new ForumAPI(dispatch, this.userId);
    this.bucket = new S3Bucket(S3_BUCKETS.BUG_REPORTS, this.forumAPI);
    this.dispatch = dispatch;
  }

  private getUserData() {
    return `Bug report from ${longDisplayName(this.user)} (userId: ${this.userId})`;
  }

  private getOtherUsersInformation() {
    const otherPresentUsers = Object.values(this.otherUsers).filter(
      (user) => user.is_present && user.id !== this.userId,
    );
    otherPresentUsers.sort(stringComparatorGenerator(longDisplayName));
    return otherPresentUsers
      .map((user) => {
        return `${longDisplayName(user)} - ${this.getS3Url(user.id)}`;
      })
      .join("\n");
  }

  private getClassData() {
    return `Class: ${this.classInfo.title} (classId: ${this.classInfo.id})`;
  }

  private getActionLogs() {
    const actionLogs = this.logger.actionLogArray();
    return actionLogs.map((actionLog) => Logger.prettyPrintActionLog(actionLog)).join("\n");
  }

  private getConsoleLogs() {
    const logs = this.logger.consoleLogArray();
    return logs.map((log) => Logger.prettyPrintLog(log)).join("\n");
  }

  private getConsoleLogsByType(type: string) {
    const logs = this.logger.consoleLogArray();
    return logs
      .filter((log) => log.type === type)
      .map((log) => Logger.prettyPrintLog(log))
      .join("\n");
  }

  private getPostKey() {
    // Use UTC to ensure all clients generate the same filename regardless of timezone
    const isoDate = new Date(this.timestamp).toISOString();
    const datePart = isoDate
      .replace("T", "-")
      .replace(/:/g, "-")
      .replace(/\.\d+Z/, "");
    return `-${this.classInfo.id}-flight-${datePart}`;
  }

  private getS3Key(userId: string) {
    return userId + this.getPostKey();
  }

  private getS3Url(userId: string) {
    const baseUrl = this.bucket.getS3Url();
    return `${baseUrl}${this.getS3Key(userId)}`;
  }

  private getBugReportSubject() {
    let subject = `${this.reportTitle || this.message || "Empty bug report"} --`;
    subject += ` ${longDisplayName(this.user)} (userId: ${this.userId})`;
    subject += ` [flight - class-id: ${this.classInfo.id}]`;
    subject += ` (${format(this.timestamp, "M/d HH:mm xx")})`;
    return subject;
  }

  private getLongReport() {
    return [
      `${this.getBugReportSubject()}`,
      document.URL,
      navigator.userAgent,
      `Message:\n${this.getShortReport()}`,
      `User:\n${this.getUserData()}`,
      `Class:\n${this.getClassData()}`,
      `Other Users' Information:\n${this.getOtherUsersInformation()}`,
      `Javascript Errors:\n${this.getConsoleLogsByType("error")}`,
      `Javascript Warnings:\n${this.getConsoleLogsByType("warn")}`,
      `Redux Actions:\n${this.getActionLogs()}`,
      `Console logs:\n${this.getConsoleLogs()}`,
      `Store State:\n${this.storeStateString}`,
    ].join("\n\n");
  }

  private getShortReport() {
    const filesText =
      this.userUploadedFileUrls.length > 0
        ? `\n\nUser submitted the following files:\n${this.userUploadedFileUrls.join("\n")}`
        : "";
    return `${this.message}${filesText}`;
  }

  async uploadFiles(): Promise<void> {
    const fileUrls: string[] = [];
    const promises = this.files.map((file, index) => {
      const fileExtension = file.name.includes(".") ? `.${file.name.split(".").pop()}` : "";
      const base1Index = index + 1;
      const keyForFile = `${this.getS3Key(this.userId)}-file${base1Index}${fileExtension}`;
      return this.bucket.uploadToS3(keyForFile, file).then((s3Url) => {
        fileUrls.push(s3Url as string);
      });
    });
    const results = await Promise.allSettled(promises);
    this.userUploadedFileUrls = fileUrls;

    const failedCount = results.filter((result) => result.status === "rejected").length;
    if (failedCount) {
      const failedRate = `${failedCount}/${results.length}`;
      throw new Error(`Failed to upload ${failedRate} files.`);
    }
  }

  async sendBugReport(retry: number = 0): Promise<void> {
    const userId = this.userId;
    const logsUrl = this.getS3Url(this.userId);
    const submissionUrl = window.location.href.split("?")[0];
    const subject = this.getBugReportSubject();
    const shortReport = `${this.getShortReport()}\n\n${logsUrl}`;
    const response = await this.forumAPI.sendBugReport(
      this.classInfo.id || "-1",
      subject,
      shortReport,
      submissionUrl,
      logsUrl,
      this.userUploadedFileUrls,
      retry,
    );
    if (response.ok) {
      this.dispatch(
        createMonitoringEvent({
          type: MonitoringEventTypeType.BUG_REPORT_SUBMITTED,
          userId,
          contextData: { subject, submissionUrl, logsUrl },
        }),
      );
    }
  }

  async uploadToS3(): Promise<string> {
    const key = this.getS3Key(this.userId);
    const longReport = this.getLongReport();
    return this.bucket.uploadToS3(key, longReport);
  }
}

export { BugReporter };
